import { createSignal, For, onCleanup, onMount } from 'solid-js';
import { t } from '@/shared/i18n';

export interface FaqItem {
  q: string;
  a: string;
}

interface FeatureFaqSectionProps {
  items: FaqItem[];
  titleKey: string;
  /** Unique id for the injected JSON-LD script tag */
  scriptId: string;
}

export default function FeatureFaqSection(props: FeatureFaqSectionProps) {
  const [open, setOpen] = createSignal<number | null>(null);

  const toggle = (i: number) => setOpen((prev) => (prev === i ? null : i));

  onMount(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: props.items.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
      })),
    };

    let script = document.getElementById(
      props.scriptId
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = props.scriptId;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    onCleanup(() => {
      document.getElementById(props.scriptId)?.remove();
    });
  });

  return (
    <section class="bg-white py-16">
      <div class="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2 class="mb-8 text-3xl font-black text-slate-900">
          {t(props.titleKey)}
        </h2>
        <dl class="space-y-3">
          <For each={props.items}>
            {(item, i) => (
              <div class="rounded-xl border border-slate-200 bg-slate-50/60">
                <dt>
                  <button
                    type="button"
                    class="flex w-full items-center justify-between px-5 py-4 text-left text-base font-semibold text-slate-900"
                    aria-expanded={open() === i()}
                    onClick={() => toggle(i())}
                  >
                    <span>{item.q}</span>
                    <span
                      class="ml-4 shrink-0 text-slate-400 transition-transform duration-200"
                      classList={{ 'rotate-180': open() === i() }}
                      aria-hidden="true"
                    >
                      ▾
                    </span>
                  </button>
                </dt>
                <dd
                  class="overflow-hidden px-5 text-sm leading-relaxed text-slate-700 transition-all duration-200"
                  classList={{
                    'max-h-0 pb-0': open() !== i(),
                    'max-h-96 pb-5': open() === i(),
                  }}
                >
                  {item.a}
                </dd>
              </div>
            )}
          </For>
        </dl>
      </div>
    </section>
  );
}
